import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Loader2,
  Send,
  MessagesSquare,
} from "lucide-react";
import { VoiceRecorderButton, VoiceMessagePlayer } from "../../components/VoiceMessage";
import { PATHWAYS as ROADMAP_PATHWAYS } from "../../data/roadmapContent";
import {
  API,
  fmtDate,
} from "./shared";

export function DirectChatsTab({ authHeader }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/clients`, { headers: authHeader() });
        setClients(res.data);
      } catch {
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadThread = async (c) => {
    try {
      const res = await axios.get(`${API}/admin/dm/${encodeURIComponent(c.email)}`, {
        headers: authHeader(),
      });
      setThread(res.data);
    } catch {}
  };

  useEffect(() => {
    if (!active) return;
    loadThread(active);
    const t = setInterval(() => loadThread(active), 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.email]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  const send = async (e) => {
    e?.preventDefault();
    if (!msg.trim() || sending || !active) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/dm/${encodeURIComponent(active.email)}`,
        { content: msg },
        { headers: authHeader() }
      );
      setMsg("");
      await loadThread(active);
    } finally {
      setSending(false);
    }
  };

  const sendVoice = async (blob, ext, duration) => {
    if (!active) return;
    const fd = new FormData();
    fd.append("file", blob, `voice.${ext}`);
    fd.append("duration", String(duration));
    await axios.post(`${API}/admin/dm/${encodeURIComponent(active.email)}/voice`, fd, {
      headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
    });
    await loadThread(active);
  };

  if (active) {
    return (
      <div data-testid="dm-thread-view">
        <button
          onClick={() => setActive(null)}
          data-testid="dm-back"
          className="text-xs uppercase tracking-[0.22em] text-[#899480] hover:text-[#3F5043] mb-4"
        >
          ← All clients
        </button>
        <div className="font-serif text-2xl text-[#3F5043] mb-1">{active.full_name}</div>
        <div className="text-xs text-[#899480] mb-4">{active.email}</div>
        <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl overflow-hidden">
          <div ref={scrollRef} className="h-96 overflow-y-auto p-5 space-y-3">
            {thread.length === 0 ? (
              <div className="text-center text-[#899480] text-sm py-10">
                No messages yet — say hello to {active.full_name.split(" ")[0]}.
              </div>
            ) : (
              thread.map((m) => {
                const mine = m.author_role === "admin";
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div>
                      <div className={`text-[10px] uppercase tracking-[0.15em] font-semibold mb-1 ${mine ? "text-right text-[#875F45]" : "text-[#899480]"}`}>
                        {m.author_name}
                        <span className="font-normal normal-case tracking-normal text-[#899480]/70 ml-2">
                          {fmtDate(m.created_at)}
                        </span>
                      </div>
                      {m.kind === "voice" ? (
                        <VoiceMessagePlayer messageId={m.id} duration={m.duration} authHeader={authHeader} mine={mine} />
                      ) : (
                      <div
                        className={`inline-block px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                          mine
                            ? "bg-[#3F5043] text-[#F8F7F3] rounded-br-sm"
                            : "bg-[#EDEDEE] text-[#3F5043] rounded-bl-sm"
                        }`}
                      >
                        {m.content}
                      </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <form onSubmit={send} className="border-t border-[#BCB3A6]/40 p-3 flex items-end gap-2">
            <textarea
              data-testid="dm-input"
              rows={1}
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Message as Soarlife Team…"
              className="flex-1 resize-none bg-[#EDEDEE] rounded-2xl px-4 py-3 text-sm text-[#3F5043] placeholder-[#3F5043]/50 focus:outline-none focus:ring-2 focus:ring-[#899480] max-h-32"
            />
            <VoiceRecorderButton onSend={sendVoice} disabled={sending} testid="dm-voice-btn" />
            <button
              type="submit"
              data-testid="dm-send"
              disabled={sending || !msg.trim()}
              className="w-11 h-11 rounded-full bg-[#3F5043] text-[#F8F7F3] flex items-center justify-center hover:bg-[#875F45] transition-colors disabled:opacity-50 flex-shrink-0"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="direct-chats-tab">
      <div className="mb-6 max-w-xl">
        <div className="font-serif text-2xl text-[#3F5043] mb-1">Direct Chats</div>
        <p className="text-sm text-[#3F5043]/70 leading-relaxed">
          Private one-on-one conversations with each client — no group needed.
        </p>
      </div>
      {loading ? (
        <div className="text-center text-[#899480] py-10">
          <Loader2 size={18} className="animate-spin inline" /> Loading…
        </div>
      ) : clients.length === 0 ? (
        <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-10 text-center" data-testid="chats-empty">
          <MessagesSquare size={26} strokeWidth={1.3} className="mx-auto text-[#899480] mb-3" />
          <div className="font-serif text-xl text-[#3F5043]">No clients registered yet.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {clients.map((c) => {
            const pw = ROADMAP_PATHWAYS.find((p) => p.id === c.roadmap?.pathway);
            const items = pw
              ? [...pw.checklist, ...(pw.prepareChecklist?.items || [])]
              : [];
            const done = items.filter((i) => c.roadmap?.checklist?.[i.key]).length;
            const pct = items.length ? Math.round((done / items.length) * 100) : 0;
            return (
              <button
                key={c.id}
                data-testid={`dm-client-${c.email}`}
                onClick={() => setActive(c)}
                className="text-left p-5 rounded-3xl bg-white border border-[#BCB3A6]/40 hover:border-[#899480] transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className="font-serif text-lg text-[#3F5043] truncate">{c.full_name}</div>
                  {c.access_granted ? (
                    <span className="text-[9px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded-full bg-[#899480]/20 text-[#3F5043] flex-shrink-0">
                      Active
                    </span>
                  ) : (
                    <span className="text-[9px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded-full bg-[#BCB3A6]/30 text-[#3F5043]/60 flex-shrink-0">
                      No code
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#899480] truncate">{c.email}</div>
                {c.groups?.length > 0 && (
                  <div className="text-[10px] text-[#875F45] mt-1.5 truncate">
                    {c.groups.join(" · ")}
                  </div>
                )}
                {c.roadmap?.pathway && (
                  <div className="mt-2.5 pt-2.5 border-t border-[#BCB3A6]/30" data-testid={`client-progress-${c.email}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#115E59] truncate">
                        {pw?.title || c.roadmap.pathway}
                      </span>
                      <span className="text-[10px] font-semibold text-[#875F45] flex-shrink-0">
                        Score {c.roadmap.score}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-[#EDEDEE] overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-[#115E59] to-[#D4AF37]"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="text-[9px] text-[#899480] mt-1">
                      {done}/{items.length} pathway steps · {c.roadmap.checkins_count} check-ins
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
